/** Audio decode / encode helpers and the zero-model mid-side karaoke trick. */

export interface StereoAudio {
  ch0: Float32Array
  ch1: Float32Array
  sampleRate: number
}

/**
 * Decode any browser-supported container (mp3, m4a, wav, ogg...) to stereo
 * float samples at `sampleRate`. Decoding through an OfflineAudioContext at the
 * target rate makes the browser resample for us, which the MDX model needs
 * since it is trained at 44.1 kHz.
 */
export async function decodeAudio(file: File, sampleRate: number): Promise<StereoAudio> {
  const bytes = await file.arrayBuffer()
  const ctx = new OfflineAudioContext(2, 1, sampleRate)
  const buf = await ctx.decodeAudioData(bytes)

  const ch0 = buf.getChannelData(0)
  // Mono input: duplicate, so mid-side cancels to silence and the model still
  // gets the stereo pair it expects.
  const ch1 = buf.numberOfChannels > 1 ? buf.getChannelData(1) : ch0

  return {
    ch0: new Float32Array(ch0),
    ch1: new Float32Array(ch1),
    sampleRate: buf.sampleRate,
  }
}

/** Encode stereo float samples as a 16-bit PCM WAV blob. */
export function encodeWav(ch0: Float32Array, ch1: Float32Array, sampleRate: number): Blob {
  const frames = ch0.length
  const dataBytes = frames * 4 // 2 channels * 16 bit
  const buf = new ArrayBuffer(44 + dataBytes)
  const view = new DataView(buf)

  const ascii = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i))
  }

  ascii(0, 'RIFF')
  view.setUint32(4, 36 + dataBytes, true)
  ascii(8, 'WAVE')
  ascii(12, 'fmt ')
  view.setUint32(16, 16, true) // PCM header size
  view.setUint16(20, 1, true) // format: PCM
  view.setUint16(22, 2, true) // channels
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 4, true) // byte rate
  view.setUint16(32, 4, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  ascii(36, 'data')
  view.setUint32(40, dataBytes, true)

  let off = 44
  for (let i = 0; i < frames; i++) {
    view.setInt16(off, toPcm16(ch0[i]), true)
    view.setInt16(off + 2, toPcm16(ch1[i]), true)
    off += 4
  }

  return new Blob([buf], { type: 'audio/wav' })
}

function toPcm16(x: number): number {
  const c = Math.max(-1, Math.min(1, x))
  return c < 0 ? c * 0x8000 : c * 0x7fff
}
